type TopLevelError = string | { base?: string | null } | null | undefined;

export function extractTopLevelErrorMessages(errors: TopLevelError[] | null | undefined): string[] {
  if (!errors?.length) return [];

  return errors
    .map((error) => {
      if (typeof error === "string") return error.trim();
      if (error && typeof error === "object" && typeof error.base === "string") {
        return error.base.trim();
      }
      return "";
    })
    .filter(Boolean);
}

export function humanizeRosterFileError(message: string): string {
  if (/empty file or invalid format/i.test(message)) {
    if (/could not find the required column headers/i.test(message)) {
      return message;
    }
    return "We couldn't read this file. It may be empty, or the column headers don't match the roster template. Download the template, confirm the headers match exactly, and try again.";
  }

  if (/missing required headers/i.test(message)) {
    if (/download the roster template/i.test(message)) {
      return message;
    }
    return `${message} Download the template and match the column headers exactly.`;
  }

  if (/malformed csv/i.test(message)) {
    return `This file couldn't be parsed as a CSV (${message.replace(/^Malformed CSV:\s*/i, "")}). Save it as a CSV (not an Excel workbook) and try again.`;
  }

  if (/invalid file type/i.test(message)) {
    return message.includes("Excel") ? message : "Invalid file type. Please upload a CSV file (not an Excel workbook).";
  }

  return message;
}

export function buildRosterUploadFeedback({
  topLevelErrors,
  validCount,
  errorCount,
  warningCount
}: {
  topLevelErrors: string[];
  validCount: number;
  errorCount: number;
  warningCount: number;
}): string | null {
  const hasPreviewRows = validCount > 0 || errorCount > 0 || warningCount > 0;

  if (!hasPreviewRows && topLevelErrors.length > 0) {
    return topLevelErrors.map(humanizeRosterFileError).join(" ");
  }

  if (!hasPreviewRows) {
    return "No student rows were found in this file. Make sure it matches the downloadable template (column headers and data rows) and try again.";
  }

  // Preview-row validation (including all-rows-failed) is surfaced via
  // getRosterValidationGuidanceBanner, not this dialog/page error string.
  return null;
}

export type RosterValidationGuidanceBanner = {
  variant: "warn" | "error";
  description: string;
  testId: string;
};

export function getRosterValidationGuidanceBanner({
  hasErrors,
  hasWarnings,
  validCount = 0,
  errorCount = 0
}: {
  hasErrors: boolean;
  hasWarnings: boolean;
  validCount?: number;
  errorCount?: number;
}): RosterValidationGuidanceBanner | null {
  if (hasErrors) {
    // Fold the all-rows-failed row-count into guidance so it is not stored as a page
    // error and then hidden behind this same error-variant banner.
    // Only claim "none passed" when every preview row has errors — warning-only
    // rows are still finalizable once errors are fixed, so exclude that mix.
    const allRowsFailed = validCount === 0 && errorCount > 0 && !hasWarnings;
    const rowLabel = errorCount === 1 ? "1 row has" : `${errorCount} rows have`;
    return {
      variant: "error",
      description: allRowsFailed
        ? `None of the rows in this file passed validation (${rowLabel} errors). Rows marked as errors must be fixed before you can finalize this roster. Correct the issues in your CSV and upload the file again.`
        : "Rows marked as errors must be fixed before you can finalize this roster. Correct the issues in your CSV and upload the file again.",
      testId: "roster-validation-error-guidance-banner"
    };
  }

  if (hasWarnings) {
    return {
      variant: "warn",
      description:
        "Warnings are OK — you can still finalize this roster. PGA HQ staff will review those students during enrollment.",
      testId: "roster-validation-warning-guidance-banner"
    };
  }

  return null;
}
